// SVG-графика разбора: win%-граф партии и диаграмма позиции.
// Порт evalGraphSvg/boardSvg из app.js v3 (~2430–2521). Чистые строки SVG.
import type { KeyMoment } from './accuracy';
import { EvalStore, nodeEvalKey, winPct } from './evals';
import { pathToRoot } from './tree';
import { Q_COLOR } from './classify';

const MOMENT_COLORS = Q_COLOR as Record<string, string>;

/** Area-chart win% по полуходам; точки — ключевые ходы. */
export function evalGraphSvg(graph: number[], moments: KeyMoment[], evals: EvalStore): string {
  const W = 680;
  const H = 110;
  const n = graph.length;
  if (n < 2) return '';
  const X = (i: number) => (i / (n - 1)) * W;
  const Y = (wp: number) => H - (wp / 100) * H;
  let line = `M0 ${Y(graph[0]!).toFixed(1)}`;
  for (let i = 1; i < n; i++) line += ` L${X(i).toFixed(1)} ${Y(graph[i]!).toFixed(1)}`;
  const dots = (moments || [])
    .map((m) => {
      const ply = pathToRoot(m.node).length - 1;
      if (ply < 0 || ply >= n) return '';
      const cpW = evals.get(nodeEvalKey(m.node));
      return `<circle cx="${X(ply).toFixed(1)}" cy="${Y(cpW != null ? winPct(cpW) : 50).toFixed(1)}" r="3.4" fill="${MOMENT_COLORS[m.cls] || '#888'}" stroke="#fff" stroke-width="0.8"/>`;
    })
    .join('');
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" class="egraph">` +
    `<rect width="${W}" height="${H}" fill="#3a3733"/><path d="${line} L${W} ${H} L0 ${H} Z" fill="#eef0ec"/>` +
    `<line x1="0" y1="${H / 2}" x2="${W}" y2="${H / 2}" stroke="#9a9a9a" stroke-dasharray="3 4" stroke-width="0.8"/>` +
    `<path d="${line}" fill="none" stroke="#2f6f4f" stroke-width="1.3"/>${dots}</svg>`
  );
}

const SVG_SOLID: Record<string, string> = { k: '♚', q: '♛', r: '♜', b: '♝', n: '♞', p: '♟' };

interface ArrowMove {
  from?: string;
  to?: string;
}

function svgSquareCenter(sq: string, sz: number): { x: number; y: number } | null {
  const file = 'abcdefgh'.indexOf(String(sq || '')[0] ?? '');
  const rank = Number(String(sq || '')[1]);
  if (file < 0 || rank < 1 || rank > 8) return null;
  return { x: file * sz + sz / 2, y: (8 - rank) * sz + sz / 2 };
}

function svgArrow(move: ArrowMove | null, sz: number, color: string, markerId: string): string {
  if (!move || !move.from || !move.to) return '';
  const from = svgSquareCenter(move.from, sz);
  const to = svgSquareCenter(move.to, sz);
  if (!from || !to) return '';
  return `<line x1="${from.x}" y1="${from.y}" x2="${to.x}" y2="${to.y}" stroke="${color}" stroke-width="5" stroke-linecap="round" opacity="0.85" marker-end="url(#${markerId})"/>`;
}

/** Диаграмма позиции из FEN: оранжевая стрелка — сыгранный ход, зелёная — лучший. */
export function boardSvg(fen: string, played: ArrowMove | null = null, best: ArrowMove | null = null): string {
  const grid = fen
    .split(' ')[0]!
    .split('/')
    .map((row) => {
      const r: string[] = [];
      for (const ch of row) {
        if (ch >= '1' && ch <= '8') {
          for (let i = 0; i < +ch; i++) r.push('');
        } else r.push(ch);
      }
      return r;
    });
  const sz = 34;
  const S = sz * 8;
  const parts = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${S}" height="${S}" viewBox="0 0 ${S} ${S}">`,
    '<defs><marker id="arrow-played" markerWidth="8" markerHeight="8" refX="6" refY="3.5" orient="auto" markerUnits="strokeWidth"><path d="M0,0 L0,7 L7,3.5 z" fill="#e08a2e"/></marker><marker id="arrow-best" markerWidth="8" markerHeight="8" refX="6" refY="3.5" orient="auto" markerUnits="strokeWidth"><path d="M0,0 L0,7 L7,3.5 z" fill="#15781b"/></marker></defs>',
  ];
  for (let dr = 0; dr < 8; dr++) {
    for (let dc = 0; dc < 8; dc++) {
      const fileIdx = dc;
      const rankNum = 8 - dr; // белые снизу
      const light = (fileIdx + rankNum) % 2 === 0;
      const x = dc * sz;
      const y = dr * sz;
      parts.push(`<rect x="${x}" y="${y}" width="${sz}" height="${sz}" fill="${light ? '#E9EDCC' : '#779954'}"/>`);
      const piece = grid[8 - rankNum]![fileIdx]!;
      if (piece) {
        const wp = piece === piece.toUpperCase();
        parts.push(
          `<text x="${x + sz / 2}" y="${y + sz * 0.74}" font-size="${sz * 0.84}" text-anchor="middle" fill="${wp ? '#fafafa' : '#181818'}" stroke="#181818" stroke-width="${wp ? 0.7 : 0.4}" font-family="'Apple Symbols','Segoe UI Symbol','Noto Sans Symbols 2',serif">${SVG_SOLID[piece.toLowerCase()]}</text>`,
        );
      }
    }
  }
  parts.push(svgArrow(best, sz, '#15781b', 'arrow-best'));
  parts.push(svgArrow(played, sz, '#e08a2e', 'arrow-played'));
  parts.push('</svg>');
  return parts.join('');
}
